import { useState, useMemo, useRef, useEffect } from 'react'
import { useEconomyStore } from '@/state/useEconomyStore'
import { buyFromMarket, sellToMarket, getMarketPrices } from '@/economy/api'
import { ITEM_DISPLAY_NAMES, getSupplyLevel, formatFollis } from '@/economy/types'
import type { WalletInfo, ItemType, MarketPriceInfo } from '@/economy/types'
import type { InteractionTarget } from '@/state/useEconomyStore'

// ═══════════════════════════════════════════════════════════════
// AMM TERMINAL — Medieval Market Interface
// ═══════════════════════════════════════════════════════════════

const IC: Record<string, string> = {
  wheat: '\u{1F33E}', sheep: '\u{1F411}', chicken: '\u{1F414}', bronze: '\u{1F7E4}',
  iron: '\u2699\uFE0F', gold: '\u2728', wood: '\u{1FAB5}', fruit: '\u{1F34E}',
  flour: '\u{1FAD8}', bread: '\u{1F35E}', ale: '\u{1F37A}', wool: '\u{1F9F6}',
  mutton: '\u{1F356}', planks: '\u{1F4CF}', charcoal: '\u2B1B', nails: '\u{1F4CC}',
  bronze_tools: '\u{1F527}', iron_tools: '\u2692\uFE0F', weapons: '\u2694\uFE0F',
  armor: '\u{1F6E1}\uFE0F', wine: '\u{1F377}', leather: '\u{1F45F}', cloth: '\u{1F9F5}',
  sails: '\u26F5', eggs: '\u{1F95A}', feathers: '\u{1FAB6}', poultry: '\u{1F357}',
  arrows: '\u{1F3F9}', bronze_fittings: '\u{1F529}', ship_hardware: '\u2693',
  nails_2: '\u{1F4CC}', chains: '\u26D3\uFE0F', jewelry: '\u{1F48D}',
  gilded_decor: '\u{1F451}', barrels: '\u{1F6E2}\uFE0F', ship_parts: '\u{1F6F6}',
  preserves: '\u{1F36F}', dried_fruit: '\u{1F347}', ship_repair_kit: '\u{1F6E0}\uFE0F',
}

// ─── Sparkline Component ─────────────────────────────────────
function Spark({ data, w = 180, h = 24, color = '#C9A84C', fill = false }: {
  data: number[]; w?: number; h?: number; color?: string; fill?: boolean
}) {
  if (!data || data.length < 2) return <svg width={w} height={h} />
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1
  const pts = data.map((v, i) => [
    ((i / (data.length - 1)) * (w - 2)) + 1,
    h - 1 - ((v - min) / range) * (h - 3),
  ])
  const line = pts.map(p => p.join(',')).join(' ')
  const area = `0,${h - 1} ${line} ${w - 1},${h - 1}`
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      {fill && <polygon points={area} fill={color} opacity="0.15" />}
      <polyline points={line} fill="none" stroke={color} strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  )
}

// ─── Depth Chart ─────────────────────────────────────────────
function DepthChart({ price, reserve, liquidity, w = 370, h = 60 }: {
  price: number; reserve: number; liquidity: number; w?: number; h?: number
}) {
  const k = reserve * liquidity
  const bidPts: { q: number; p: number }[] = []
  const askPts: { q: number; p: number }[] = []
  for (let i = 1; i <= 15; i++) {
    const q = i * (reserve * 0.05)
    // Sell (bid): ir + q → price drops
    const newIr = reserve + q
    const sellP = (liquidity - k / newIr) / q
    if (sellP > 0) bidPts.push({ q, p: sellP })
    // Buy (ask): ir - q → price rises
    if (q < reserve * 0.8) {
      const buyIr = reserve - q
      const buyP = (k / buyIr - liquidity) / q
      if (buyP > 0 && isFinite(buyP)) askPts.push({ q, p: buyP })
    }
  }
  const allP = [...bidPts.map(p => p.p), ...askPts.map(p => p.p)].filter(p => isFinite(p))
  if (allP.length < 2) return null
  const minP = Math.min(...allP) * 0.8, maxP = Math.max(...allP) * 1.1, rangeP = maxP - minP || 1
  const maxQ = Math.max(...bidPts.map(p => p.q), ...askPts.map(p => p.q))
  const toX = (q: number) => (q / maxQ) * (w / 2 - 5)
  const toY = (p: number) => h - 4 - ((p - minP) / rangeP) * (h - 8)
  const bidLine = bidPts.map(p => `${w / 2 - toX(p.q)},${toY(p.p)}`).join(' ')
  const askLine = askPts.map(p => `${w / 2 + toX(p.q)},${toY(p.p)}`).join(' ')
  const spotY = toY(price)

  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <line x1={w / 2} y1="0" x2={w / 2} y2={h} stroke="#3a3226" strokeWidth="0.5" />
      {bidPts.length > 1 && <>
        <polygon points={`${w / 2},${spotY} ${bidLine} ${w / 2 - toX(bidPts[bidPts.length - 1].q)},${h - 4}`} fill="rgba(107,142,90,0.15)" />
        <polyline points={bidLine} fill="none" stroke="#6B8E5A" strokeWidth="1" />
      </>}
      {askPts.length > 1 && <>
        <polygon points={`${w / 2},${spotY} ${askLine} ${w / 2 + toX(askPts[askPts.length - 1].q)},${h - 4}`} fill="rgba(166,92,76,0.15)" />
        <polyline points={askLine} fill="none" stroke="#A65C4C" strokeWidth="1" />
      </>}
      <circle cx={w / 2} cy={spotY} r="2.5" fill="#C9A84C" />
      <text x={w / 2 + 5} y={spotY - 3} fill="#8B7D6B" fontSize="7" fontFamily="monospace">{price.toFixed(2)}</text>
      <text x="3" y={h - 1} fill="#6B8E5A" fontSize="6">BIDS</text>
      <text x={w - 22} y={h - 1} fill="#A65C4C" fontSize="6">ASKS</text>
    </svg>
  )
}

// ─── Pool Curve ──────────────────────────────────────────────
function PoolCurve({ reserve, liquidity, w = 370, h = 50 }: {
  reserve: number; liquidity: number; w?: number; h?: number
}) {
  const k = reserve * liquidity
  const maxX = reserve * 3
  const pts: string[] = []
  for (let i = 1; i <= 50; i++) {
    const x = (i / 50) * maxX
    const y = k / x
    pts.push(`${(x / maxX) * (w - 10) + 5},${h - 4 - Math.min((y / (liquidity * 3)) * (h - 10), h - 8)}`)
  }
  const cx = (reserve / maxX) * (w - 10) + 5
  const cy = h - 4 - Math.min((liquidity / (liquidity * 3)) * (h - 10), h - 8)
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={pts.join(' ')} fill="none" stroke="#3a3226" strokeWidth="1.2" />
      <circle cx={cx} cy={cy} r="3" fill="#C9A84C" stroke="#D4C4A0" strokeWidth="0.7" />
      <text x={cx > w / 2 ? cx - 4 : cx + 5} y={cy - 5} fill="#6B5E50" fontSize="7" fontFamily="monospace" textAnchor={cx > w / 2 ? 'end' : 'start'}>
        ({reserve.toFixed(0)}, {liquidity.toFixed(0)})
      </text>
      <text x={w / 2} y={h} fill="#3a3226" fontSize="6" textAnchor="middle" fontFamily="monospace">x*y = {(k / 1000).toFixed(1)}k</text>
    </svg>
  )
}

// ─── Slippage Table ──────────────────────────────────────────
function SlippageTable({ price, reserve, liquidity }: {
  price: number; reserve: number; liquidity: number
}) {
  const k = reserve * liquidity
  const rows = [1, 5, 10, 20, 50]
  return (
    <div style={{ fontSize: 9, fontFamily: 'monospace' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, color: '#3a3226', marginBottom: 2 }}>
        <span>QTY</span><span>BUY</span><span>SELL</span>
      </div>
      {rows.map(q => {
        const buyOk = q < reserve * 0.9
        const buyTotal = buyOk ? (k / (reserve - q) - liquidity) : 0
        const buyUnit = buyOk ? buyTotal / q : 0
        const buySlip = buyOk ? (buyUnit / price - 1) : 0
        const sellIr = reserve + q
        const sellTotal = liquidity - k / sellIr
        const sellUnit = sellTotal / q
        const sellSlip = 1 - sellUnit / price
        return (
          <div key={q} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, color: '#5a4d3a', borderTop: '1px solid #1a1611', padding: '1px 0' }}>
            <span style={{ color: '#6B5E50' }}>{q}x</span>
            <span style={{ color: buyOk ? (buySlip > 0.05 ? '#A65C4C' : '#6B8E5A') : '#2a2520' }}>
              {buyOk ? `${buyUnit.toFixed(1)} (${(buySlip * 100).toFixed(1)}%)` : '-'}
            </span>
            <span style={{ color: '#6B5E50' }}>{sellUnit.toFixed(1)} ({(sellSlip * 100).toFixed(1)}%)</span>
          </div>
        )
      })}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────
const S = {
  box: { background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(200,184,154,0.08)', borderRadius: 6, padding: 8, marginBottom: 6 } as const,
  label: { fontSize: 9, color: '#4a3f30', textTransform: 'uppercase' as const, letterSpacing: 1.5, marginBottom: 4, fontWeight: 600 },
  stat: { fontSize: 18, fontWeight: 700, color: '#D4C4A0', fontFamily: "'JetBrains Mono', monospace", letterSpacing: -0.5 } as const,
  substat: { fontSize: 10, color: '#6B5E50', fontFamily: 'monospace' } as const,
  sectionHead: { fontSize: 10, color: '#5a4d3a', textTransform: 'uppercase' as const, letterSpacing: 2, borderBottom: '1px solid #2a2520', paddingBottom: 3, marginBottom: 6, fontWeight: 600 } as const,
}

// ═══════════════════════════════════════════════════════════════
// MAIN PANEL
// ═══════════════════════════════════════════════════════════════

interface PanelProps {
  interaction: InteractionTarget
  wallet: WalletInfo | null
}

export function TradingPostPanel({ interaction, wallet }: PanelProps) {
  const [tab, setTab] = useState<'market' | 'trade'>('market')
  const [tradeMode, setTradeMode] = useState<'buy' | 'sell'>('buy')
  const [selectedItem, setSelectedItem] = useState<ItemType | ''>('')
  const [quantity, setQuantity] = useState(1)
  const priceHistoryRef = useRef<Map<ItemType, number[]>>(new Map())

  const villageData = useEconomyStore((s) => s.villageData.get(interaction.villageIndex))
  const tradeLoading = useEconomyStore((s) => s.tradeLoading)
  const tradeError = useEconomyStore((s) => s.tradeError)
  const setTradeLoading = useEconomyStore((s) => s.setTradeLoading)
  const setTradeError = useEconomyStore((s) => s.setTradeError)
  const updateCargoFromTrade = useEconomyStore((s) => s.updateCargoFromTrade)
  const setMarketPrices = useEconomyStore((s) => s.setMarketPrices)

  const prices = villageData?.marketPrices ?? []
  const priceMap = useMemo(() => {
    const m = new Map<ItemType, MarketPriceInfo>()
    for (const p of prices) m.set(p.item, p)
    return m
  }, [prices])

  // Track price history
  useEffect(() => {
    for (const p of prices) {
      const hist = priceHistoryRef.current.get(p.item) ?? []
      hist.push(p.spot_price)
      if (hist.length > 60) hist.shift()
      priceHistoryRef.current.set(p.item, hist)
    }
  }, [prices])

  // Auto-refresh prices
  useEffect(() => {
    if (interaction.serverId === null) return
    const iv = setInterval(async () => {
      try {
        const newPrices = await getMarketPrices(interaction.serverId!)
        setMarketPrices(interaction.villageIndex, newPrices)
      } catch { /* ignore */ }
    }, 8000)
    return () => clearInterval(iv)
  }, [interaction.serverId, interaction.villageIndex, setMarketPrices])

  if (interaction.serverId === null) {
    return <p className="panel-placeholder">This settlement has no active market.</p>
  }

  const selectedPrice = selectedItem ? priceMap.get(selectedItem) : null
  const estimatedCost = selectedPrice && tradeMode === 'buy'
    ? Math.ceil(selectedPrice.spot_price * quantity * 1.05)
    : 0
  const estimatedRevenue = selectedPrice && tradeMode === 'sell'
    ? Math.floor(selectedPrice.spot_price * quantity * 0.95)
    : 0
  const cargoMap = new Map(wallet?.cargo ?? [])

  async function handleTrade() {
    if (!selectedItem || !interaction.serverId) return
    setTradeLoading(true)
    setTradeError(null)
    try {
      if (tradeMode === 'buy') {
        const maxPrice = Math.ceil(estimatedCost * 1.2)
        const result = await buyFromMarket(interaction.serverId, selectedItem, quantity, maxPrice)
        updateCargoFromTrade(selectedItem, result.new_item_qty, result.new_follis_balance)
      } else {
        const result = await sellToMarket(interaction.serverId, selectedItem, quantity, 0)
        updateCargoFromTrade(selectedItem, result.new_item_qty, result.new_follis_balance)
      }
      const newPrices = await getMarketPrices(interaction.serverId)
      setMarketPrices(interaction.villageIndex, newPrices)
    } catch (e: any) {
      setTradeError(e.message || 'Trade failed')
    }
    setTradeLoading(false)
  }

  // Sort prices by category
  const sortedPrices = useMemo(() =>
    [...prices].sort((a, b) => a.spot_price - b.spot_price),
  [prices])

  const totalLiquidity = prices.reduce((s, p) => s + p.pool_currency_reserve, 0)
  const avgPrice = prices.length ? prices.reduce((s, p) => s + p.spot_price, 0) / prices.length : 0

  return (
    <div style={{ fontFamily: "'Crimson Text', serif" }}>
      {/* Terminal header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, padding: '0 2px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 5, height: 5, borderRadius: 3, background: '#6B8E5A', animation: 'pulse 1s infinite' }} />
          <span style={{ fontSize: 9, color: '#3a3226', fontFamily: 'monospace', letterSpacing: 1 }}>LIVE</span>
        </div>
        <div style={{ display: 'flex', gap: 3 }}>
          {(['market', 'trade'] as const).map(t => (
            <button key={t} onClick={() => { setTab(t); setTradeError(null) }} style={{
              background: tab === t ? '#1a1611' : 'transparent',
              border: `1px solid ${tab === t ? '#3a3226' : 'transparent'}`,
              color: tab === t ? '#D4C4A0' : '#3a3226',
              borderRadius: 3, padding: '3px 12px', fontSize: 9, letterSpacing: 1.5, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>{t.toUpperCase()}</button>
          ))}
        </div>
      </div>

      {/* Key metrics row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginBottom: 6 }}>
        <div style={S.box}>
          <div style={S.label}>ITEMS</div>
          <div style={{ ...S.stat, fontSize: 16 }}>{prices.length}</div>
        </div>
        <div style={S.box}>
          <div style={S.label}>LIQUIDITY</div>
          <div style={{ ...S.stat, fontSize: 16 }}>{(totalLiquidity / 1000).toFixed(1)}k</div>
        </div>
        <div style={S.box}>
          <div style={S.label}>AVG PRICE</div>
          <div style={{ ...S.stat, fontSize: 16 }}>{avgPrice.toFixed(1)}<span style={{ fontSize: 9, color: '#4a3f30' }}></span></div>
        </div>
      </div>

      {tradeError && <div style={{ background: 'rgba(166,92,76,0.15)', border: '1px solid #A65C4C', borderRadius: 4, padding: '6px 8px', fontSize: 11, color: '#A65C4C', marginBottom: 6 }}>{tradeError}</div>}

      {/* ═══ MARKET TAB ═══ */}
      {tab === 'market' && (
        <>
          {/* Selected item detail */}
          {selectedPrice && (
            <div style={S.box}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 14, color: '#D4C4A0', fontWeight: 700 }}>
                  {IC[selectedItem] || '\u{1F4E6}'} {ITEM_DISPLAY_NAMES[selectedItem as ItemType]}
                </span>
                <span style={{ ...S.stat, fontSize: 20 }}>{selectedPrice.spot_price.toFixed(2)}<span style={{ fontSize: 10, color: '#4a3f30' }}></span></span>
              </div>

              {/* Sparkline */}
              <div style={S.sectionHead}>PRICE HISTORY</div>
              <Spark
                data={priceHistoryRef.current.get(selectedItem as ItemType) ?? [selectedPrice.spot_price]}
                w={370} h={35} color="#C9A84C" fill
              />

              {/* Pool stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 4, margin: '8px 0' }}>
                {[
                  { l: 'RESERVE', v: selectedPrice.pool_item_reserve.toFixed(0) },
                  { l: 'LIQUIDITY', v: selectedPrice.pool_currency_reserve.toFixed(0) + '\u0192' },
                  { l: 'K-VALUE', v: ((selectedPrice.pool_item_reserve * selectedPrice.pool_currency_reserve) / 1000).toFixed(1) + 'k' },
                  { l: 'SUPPLY', v: getSupplyLevel(selectedPrice.pool_item_reserve).toUpperCase(), c: getSupplyLevel(selectedPrice.pool_item_reserve) === 'scarce' ? '#A65C4C' : getSupplyLevel(selectedPrice.pool_item_reserve) === 'low' ? '#C9A84C' : '#6B8E5A' },
                ].map((s, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 7, color: '#3a3226', letterSpacing: 1 }}>{s.l}</div>
                    <div style={{ fontSize: 12, color: (s as any).c || '#D4C4A0', fontFamily: 'monospace' }}>{s.v}</div>
                  </div>
                ))}
              </div>

              {/* Depth chart */}
              <div style={S.sectionHead}>DEPTH CHART</div>
              <DepthChart
                price={selectedPrice.spot_price}
                reserve={selectedPrice.pool_item_reserve}
                liquidity={selectedPrice.pool_currency_reserve}
              />

              {/* Pool curve */}
              <div style={{ ...S.sectionHead, marginTop: 8 }}>AMM CURVE (x*y=k)</div>
              <PoolCurve
                reserve={selectedPrice.pool_item_reserve}
                liquidity={selectedPrice.pool_currency_reserve}
              />

              {/* Slippage table */}
              <div style={{ ...S.sectionHead, marginTop: 8 }}>SLIPPAGE TABLE</div>
              <SlippageTable
                price={selectedPrice.spot_price}
                reserve={selectedPrice.pool_item_reserve}
                liquidity={selectedPrice.pool_currency_reserve}
              />

              {/* Quick trade buttons */}
              <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                <button onClick={() => { setTab('trade'); setTradeMode('buy') }} style={{
                  flex: 1, padding: '6px 0', fontSize: 11, fontWeight: 700, border: '1px solid #6B8E5A',
                  background: 'rgba(107,142,90,0.1)', color: '#6B8E5A', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
                }}>BUY</button>
                <button onClick={() => { setTab('trade'); setTradeMode('sell') }} style={{
                  flex: 1, padding: '6px 0', fontSize: 11, fontWeight: 700, border: '1px solid #A65C4C',
                  background: 'rgba(166,92,76,0.1)', color: '#A65C4C', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
                }}>SELL</button>
              </div>
            </div>
          )}

          {/* All items grid */}
          <div style={S.sectionHead}>{selectedItem ? 'ALL ITEMS' : 'SELECT AN ITEM'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            {sortedPrices.map((p) => {
              const hist = priceHistoryRef.current.get(p.item) ?? []
              const first = hist[0] ?? p.spot_price
              const change = ((p.spot_price - first) / first * 100)
              const isSelected = selectedItem === p.item
              return (
                <div key={p.item} onClick={() => setSelectedItem(p.item)} style={{
                  ...S.box, cursor: 'pointer', marginBottom: 0,
                  border: isSelected ? '1px solid #3a3226' : '1px solid rgba(200,184,154,0.05)',
                  background: isSelected ? 'rgba(201,168,76,0.06)' : 'rgba(0,0,0,0.25)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 10, color: '#8B7D6B' }}>{IC[p.item] || '\u{1F4E6}'} {ITEM_DISPLAY_NAMES[p.item]}</span>
                    {hist.length > 1 && (
                      <span style={{ fontSize: 8, color: change > 0 ? '#A65C4C' : '#6B8E5A', fontFamily: 'monospace' }}>
                        {change > 0 ? '+' : ''}{change.toFixed(1)}%
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#D4C4A0', fontFamily: "'JetBrains Mono', monospace" }}>
                    {p.spot_price.toFixed(2)}<span style={{ fontSize: 9, color: '#4a3f30' }}></span>
                  </div>
                  {hist.length > 1 && <Spark data={hist} w={160} h={18} color={change > 0 ? '#A65C4C' : '#6B8E5A'} fill />}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 7, color: '#3a3226', marginTop: 2 }}>
                    <span>res: {p.pool_item_reserve.toFixed(0)}</span>
                    <span>liq: {p.pool_currency_reserve.toFixed(0)}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* ═══ TRADE TAB ═══ */}
      {tab === 'trade' && (
        <>
          {/* Buy/Sell toggle */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {(['buy', 'sell'] as const).map(m => (
              <button key={m} onClick={() => { setTradeMode(m); setSelectedItem(''); setTradeError(null) }} style={{
                flex: 1, padding: '8px 0', fontSize: 12, fontWeight: 700, letterSpacing: 1,
                border: `1px solid ${tradeMode === m ? (m === 'buy' ? '#6B8E5A' : '#A65C4C') : '#2a2520'}`,
                background: tradeMode === m ? (m === 'buy' ? 'rgba(107,142,90,0.12)' : 'rgba(166,92,76,0.12)') : 'transparent',
                color: tradeMode === m ? (m === 'buy' ? '#6B8E5A' : '#A65C4C') : '#3a3226',
                borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
              }}>{m.toUpperCase()}</button>
            ))}
          </div>

          {tradeMode === 'buy' && (
            <>
              <div style={S.sectionHead}>MARKET PRICES</div>
              {prices.length === 0 && <p style={{ color: '#3a3226', fontStyle: 'italic', fontSize: 12 }}>Loading prices...</p>}
              {sortedPrices.map((p) => {
                const level = getSupplyLevel(p.pool_item_reserve)
                const levelColor = level === 'scarce' ? '#A65C4C' : level === 'low' ? '#C9A84C' : '#6B8E5A'
                return (
                  <div key={p.item} onClick={() => { setSelectedItem(p.item); setQuantity(1) }} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '5px 6px', borderRadius: 4, marginBottom: 2, cursor: 'pointer',
                    background: selectedItem === p.item ? 'rgba(201,168,76,0.08)' : 'transparent',
                    border: selectedItem === p.item ? '1px solid #3a3226' : '1px solid transparent',
                  }}>
                    <span style={{ fontSize: 11, color: selectedItem === p.item ? '#D4C4A0' : '#6B5E50' }}>
                      {IC[p.item] || '\u{1F4E6}'} {ITEM_DISPLAY_NAMES[p.item]}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 8, color: levelColor, fontFamily: 'monospace' }}>{level}</span>
                      <span style={{ fontSize: 12, color: '#D4C4A0', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{p.spot_price.toFixed(1)}</span>
                    </span>
                  </div>
                )
              })}

              {selectedItem && selectedPrice && (
                <div style={{ ...S.box, marginTop: 8 }}>
                  <div style={S.sectionHead}>PURCHASE ORDER</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#D4C4A0', fontSize: 14, fontWeight: 700 }}>
                      {IC[selectedItem] || '\u{1F4E6}'} {ITEM_DISPLAY_NAMES[selectedItem as ItemType]}
                    </span>
                    <span style={{ color: '#8B7D6B', fontSize: 11, fontFamily: 'monospace' }}>
                      @ {selectedPrice.spot_price.toFixed(2)} ea
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ color: '#6B5E50', fontSize: 11 }}>Qty:</span>
                    <input type="number" value={quantity} min={1} max={100}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{
                        width: 60, padding: '4px 8px', background: '#1a1611', border: '1px solid #3a3226',
                        borderRadius: 3, color: '#D4C4A0', fontFamily: 'monospace', fontSize: 14, textAlign: 'center',
                      }}
                    />
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <div style={{ fontSize: 9, color: '#4a3f30' }}>ESTIMATED COST</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#D4C4A0', fontFamily: "'JetBrains Mono', monospace" }}>
                        {formatFollis(estimatedCost)}
                      </div>
                    </div>
                  </div>
                  <button onClick={handleTrade} disabled={tradeLoading || !wallet || wallet.follis < estimatedCost} style={{
                    width: '100%', padding: '8px 0', fontSize: 12, fontWeight: 700, border: '1px solid #6B8E5A',
                    background: 'rgba(107,142,90,0.15)', color: '#6B8E5A', borderRadius: 4, cursor: 'pointer',
                    fontFamily: 'inherit', letterSpacing: 1, opacity: (tradeLoading || !wallet || wallet.follis < estimatedCost) ? 0.4 : 1,
                  }}>
                    {tradeLoading ? 'EXECUTING...' : `BUY ${quantity}x ${ITEM_DISPLAY_NAMES[selectedItem as ItemType]}`}
                  </button>
                </div>
              )}
            </>
          )}

          {tradeMode === 'sell' && (
            <>
              <div style={S.sectionHead}>YOUR CARGO</div>
              {(!wallet || wallet.cargo.length === 0) && <p style={{ color: '#3a3226', fontStyle: 'italic', fontSize: 12, padding: 8 }}>Empty hold.</p>}
              {wallet?.cargo.map(([item, qty]) => {
                const mp = priceMap.get(item)
                return (
                  <div key={item} onClick={() => { setSelectedItem(item); setQuantity(Math.min(qty, quantity || 1)) }} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '5px 6px', borderRadius: 4, marginBottom: 2, cursor: 'pointer',
                    background: selectedItem === item ? 'rgba(201,168,76,0.08)' : 'transparent',
                    border: selectedItem === item ? '1px solid #3a3226' : '1px solid transparent',
                  }}>
                    <span style={{ fontSize: 11, color: selectedItem === item ? '#D4C4A0' : '#6B5E50' }}>
                      {IC[item] || '\u{1F4E6}'} {ITEM_DISPLAY_NAMES[item]} <span style={{ color: '#4a3f30' }}>x{qty}</span>
                    </span>
                    <span style={{ fontSize: 12, color: '#D4C4A0', fontFamily: "'JetBrains Mono', monospace" }}>
                      {mp ? `${mp.spot_price.toFixed(1)}` : '-'}
                    </span>
                  </div>
                )
              })}

              {selectedItem && cargoMap.has(selectedItem) && selectedPrice && (
                <div style={{ ...S.box, marginTop: 8 }}>
                  <div style={S.sectionHead}>SELL ORDER</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#D4C4A0', fontSize: 14, fontWeight: 700 }}>
                      {IC[selectedItem] || '\u{1F4E6}'} {ITEM_DISPLAY_NAMES[selectedItem as ItemType]}
                    </span>
                    <span style={{ color: '#8B7D6B', fontSize: 11, fontFamily: 'monospace' }}>
                      @ {selectedPrice.spot_price.toFixed(2)} ea
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ color: '#6B5E50', fontSize: 11 }}>Qty:</span>
                    <input type="number" value={quantity} min={1} max={cargoMap.get(selectedItem) || 1}
                      onChange={(e) => setQuantity(Math.max(1, Math.min(cargoMap.get(selectedItem) || 1, parseInt(e.target.value) || 1)))}
                      style={{
                        width: 60, padding: '4px 8px', background: '#1a1611', border: '1px solid #3a3226',
                        borderRadius: 3, color: '#D4C4A0', fontFamily: 'monospace', fontSize: 14, textAlign: 'center',
                      }}
                    />
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <div style={{ fontSize: 9, color: '#4a3f30' }}>ESTIMATED REVENUE</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#6B8E5A', fontFamily: "'JetBrains Mono', monospace" }}>
                        {formatFollis(estimatedRevenue)}
                      </div>
                    </div>
                  </div>
                  <button onClick={handleTrade} disabled={tradeLoading || quantity > (cargoMap.get(selectedItem) || 0)} style={{
                    width: '100%', padding: '8px 0', fontSize: 12, fontWeight: 700, border: '1px solid #A65C4C',
                    background: 'rgba(166,92,76,0.15)', color: '#A65C4C', borderRadius: 4, cursor: 'pointer',
                    fontFamily: 'inherit', letterSpacing: 1, opacity: (tradeLoading || quantity > (cargoMap.get(selectedItem) || 0)) ? 0.4 : 1,
                  }}>
                    {tradeLoading ? 'EXECUTING...' : `SELL ${quantity}x ${ITEM_DISPLAY_NAMES[selectedItem as ItemType]}`}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <style>{`@keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.4} }`}</style>
    </div>
  )
}
